import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate
} from 'react-router-dom';

// Constants
import { ROUTES } from '@/utils/constants/routesConstants';
import { ProtectedRoutes } from './ProtectedRoutes';

// Redux
import { setShellActive, setShellPath } from '@/store/slices/commonSlice';
import { useAppDispatch } from '@/store';
import { setUserDetails } from '@/store/slices/userSlice';

// Services
import { UserDetails } from '@/services/UserDetails';

// Helpers
import { isObjectNotEmpty } from '@/helpers/shadcn/utils';

// Error Pages
import { PageError403 } from '@/pages/Errors/403';
import { PageError500 } from '@/pages/Errors/500';
import { PageError404 } from '@/pages/Errors/404';

// Test Pages
import { TestPage } from '@/pages/TestPage';

// Store Related Components

import { StorePage } from '@/pages/Store/StorePage';
import ProfileView from '@/containers/Store/ProfileView/ProfileView';
import PaymentAndTaxView from '@/containers/Store/PaymentAndTaxView/PaymentAndTaxView';

// Profile Management Components
import ProfileManagementView from '@/pages/ProfileManagement/ProfileManagementView';

// Customer Related Components
import { CustomerPage } from '@/pages/Customer/CustomerPage';
import CustomerSettingsView from '@/containers/Customer/SettingsView/SettingsView';
import CustomerProfileView from '@/containers/Customer/ProfileView/ProfileView';

// Onboarding Status Components
import OnboardingStatusOverviewPage from '@/pages/OnboardingStatus/OnboardingStatusOverviewPage';
import OktaStatusView from '@/pages/OnboardingStatus/OktaStatusView';

import Homepage from '@/pages/Homepage/Homepage';
import MissingProductListView from '@/pages/OnboardingStatus/MissingProductListView';
import VisitListRelatedView from '@/pages/OnboardingStatus/VisitListRelatedView';
import StoresMissingDataView from '@/pages/OnboardingStatus/StoresMissingDataView';
import CustomersTable from '@/containers/ProfileManagement/CustomersTable';
import StoresTable from '@/containers/ProfileManagement/StoresTable';
import { setDarkMode } from '@/store/slices/themeSlice';

const errorRoutes = [
  { path: ROUTES.ERROR_500, element: <PageError500 /> },
  { path: ROUTES.ERROR_403, element: <PageError403 /> },
  { path: '*', element: <PageError404 /> }
];

const router = createBrowserRouter([
  {
    element: <ProtectedRoutes />,

    children: [
      {
        path: ROUTES.HOME,
        element: <Navigate to={ROUTES.PROFILE_MANAGEMENT} />
      },

      // Store Related Routes
      {
        path: ROUTES.STORE_VIEW,
        element: <StorePage />,
        children: [
          { path: '', element: <ProfileView /> },
          { path: 'payment-and-tax', element: <PaymentAndTaxView /> }
        ]
      },

      {
        path: ROUTES.PROFILE_MANAGEMENT,
        element: <ProfileManagementView />,
        children: [
          {
            path: '',
            element: <Navigate to={ROUTES.PROFILE_MANAGEMENT_CUSTOMERS} />
          },
          { path: 'customers', element: <CustomersTable /> },
          { path: 'stores', element: <StoresTable /> }
        ]
      },

      // Customer Related Routes
      {
        path: ROUTES.CUSTOMER_VIEW,
        element: <CustomerPage />,
        children: [
          { path: '', element: <CustomerProfileView /> },
          { path: 'settings', element: <CustomerSettingsView /> }
        ]
      },

      {
        path: ROUTES.ONBOARDING_STATUS,
        children: [
          {
            path: '',
            element: <OnboardingStatusOverviewPage />
          },
          {
            path: 'okta-status/:status',
            element: <OktaStatusView />
          },
          {
            path: 'missing-product-list',
            element: <MissingProductListView />
          },
          {
            path: 'missing-visit-list',
            element: <VisitListRelatedView />
          },
          {
            path: 'missing-moa',
            element: <VisitListRelatedView />
          },
          {
            path: 'missing-cut-off-time',
            element: <VisitListRelatedView />
          },
          {
            path: 'missing-delivery-service',
            element: <VisitListRelatedView />
          },
          {
            path: 'missing-delivery-days',
            element: <VisitListRelatedView />
          },
          // {
          //   path: 'missing-promo-data',
          //   element: <StoresMissingDataView />
          // },
          // {
          //   path: 'missing-activity-data',
          //   element: <StoresMissingDataView />
          // },
          {
            path: 'missing-segments-data',
            element: <StoresMissingDataView />
          },
          // {
          //   path: 'missing-main-targets-data',
          //   element: <StoresMissingDataView />
          // },
          // {
          //   path: 'missing-product-targets-data',
          //   element: <StoresMissingDataView />
          // }
        ]
      },

      /* Errors */
      ...errorRoutes,

      { path: ROUTES.TEST, element: <TestPage /> }
      /* Products */
    ]
  }
]);

export const AppRouter = ({ shellActive, darkTheme, path }) => {
  const dispatch = useAppDispatch();
  const userData = useSelector(state => state?.userDetails);
  const getUserData = async () => {
    if (!isObjectNotEmpty(userData)) {
      try {
        await UserDetails();
        const reqData = {
          name: 'Robert',
          role: ['admin', 'super-admin'],
          country: [
            { name: 'Mexico', lang: [{ langcode: 33, name: 'mexico' }] }
          ],
          countryCode: 'mx',
          siteId: 21
        };
        dispatch(setUserDetails(reqData));
      } catch {
        console.log('Error');
        const reqData = {
          name: 'Louis',
          role: ['admin'],
          country: [
            { name: 'Turkey', lang: [{ langcode: 31, name: 'Turkey' }] }
          ],
          countryCode: 'TR',
          siteId: 23
        };
        dispatch(setUserDetails(reqData));
      }
    }
  };
  useEffect(() => {
    getUserData();
  });
  useEffect(() => {
    dispatch(setShellActive(shellActive));
  }, [shellActive, dispatch]);

  useEffect(() => {
    dispatch(setDarkMode(darkTheme))
  }, [darkTheme, dispatch])

  // expected everytime to dispatch the shell path when component is mounted to navigate properly
  useEffect(() => {
    dispatch(setShellPath(path));
  });

  return <RouterProvider router={router} />;
};
